/**
 * Currency metadata for the exchange calculator.
 *
 * The catalog is served by the API (`/exchange/currencies`) so the two
 * halves of the feature can never disagree about what's supported. What
 * lives here is the presentation layer the API has no business knowing
 * about — flags, locales, number formatting — plus a hardcoded copy of
 * the catalog used as the first-paint fallback while the request is in
 * flight.
 */

/** Mirrors the `Currency` interface in `server/services/exchange-rates.ts`. */
export interface Currency {
  code: string;
  name: string;
  /** ISO 3166-1 alpha-2 — the flag is derived from this, not stored. */
  countryCode: string;
  countryName: string;
  symbol: string;
  /** Minor units. JPY/KRW are zero-decimal. */
  decimals: number;
  region: "APAC" | "EMEA" | "AMER" | string;
  /** True for the six currencies the platform issues cards in. */
  platformCurrency: boolean;
}

/**
 * ISO 3166-1 alpha-2 → regional-indicator flag emoji.
 *
 * Two letters map to two codepoints offset into the regional-indicator
 * block; the pair renders as a flag on every platform that ships them.
 * This beats bundling 20 SVGs and never goes stale when the catalog
 * grows.
 */
export function countryFlag(countryCode: string): string {
  if (!/^[A-Za-z]{2}$/.test(countryCode)) return "";
  return countryCode
    .toUpperCase()
    .split("")
    .map((char) => String.fromCodePoint(0x1f1e6 + char.charCodeAt(0) - 65))
    .join("");
}

/** Locale used to format each currency's amounts. */
const CURRENCY_LOCALES: Record<string, string> = {
  AUD: "en-AU",
  NZD: "en-NZ",
  INR: "en-IN",
  GBP: "en-GB",
  EUR: "de-DE",
  USD: "en-US",
  SGD: "en-SG",
  AED: "ar-AE",
  SAR: "ar-SA",
  CAD: "en-CA",
  JPY: "ja-JP",
  CNY: "zh-CN",
  HKD: "zh-HK",
  MYR: "ms-MY",
  PHP: "en-PH",
  THB: "th-TH",
  KRW: "ko-KR",
  CHF: "de-CH",
  SEK: "sv-SE",
  ZAR: "en-ZA",
};

export function currencyLocale(code: string): string {
  return CURRENCY_LOCALES[code] || "en-US";
}

/**
 * Format a converted amount in its own currency's convention.
 *
 * Intl throws on unknown ISO codes, so an unrecognized code falls back
 * to a plain number with the code appended rather than blanking the
 * cell.
 */
export function formatMoney(amount: number, code: string, decimals = 2): string {
  if (!Number.isFinite(amount)) return "—";
  try {
    return new Intl.NumberFormat(currencyLocale(code), {
      style: "currency",
      currency: code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);
  } catch {
    return `${amount.toFixed(decimals)} ${code}`;
  }
}

/**
 * Format a rate rather than an amount.
 *
 * Rates span six orders of magnitude across this catalog (1 JPY =
 * 0.0064 USD vs 1 USD = 1,300 KRW), so a fixed decimal count is either
 * lossy at one end or noise at the other. Scale the precision to the
 * magnitude instead.
 */
export function formatRate(rate: number): string {
  if (!Number.isFinite(rate) || rate <= 0) return "—";
  const decimals = rate >= 1000 ? 2 : rate >= 1 ? 4 : rate >= 0.01 ? 5 : 6;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(rate);
}

/**
 * First-paint fallback catalog — kept in sync with `CURRENCIES` in
 * `server/services/exchange-rates.ts`. The API response wins as soon as
 * it lands.
 */
export const FALLBACK_CURRENCIES: Currency[] = [
  { code: "AUD", name: "Australian Dollar", countryCode: "AU", countryName: "Australia", symbol: "$", decimals: 2, region: "APAC", platformCurrency: true },
  { code: "NZD", name: "New Zealand Dollar", countryCode: "NZ", countryName: "New Zealand", symbol: "$", decimals: 2, region: "APAC", platformCurrency: true },
  { code: "INR", name: "Indian Rupee", countryCode: "IN", countryName: "India", symbol: "₹", decimals: 2, region: "APAC", platformCurrency: true },
  { code: "GBP", name: "Pound Sterling", countryCode: "GB", countryName: "United Kingdom", symbol: "£", decimals: 2, region: "EMEA", platformCurrency: true },
  { code: "EUR", name: "Euro", countryCode: "EU", countryName: "European Union", symbol: "€", decimals: 2, region: "EMEA", platformCurrency: true },
  { code: "USD", name: "US Dollar", countryCode: "US", countryName: "United States", symbol: "$", decimals: 2, region: "AMER", platformCurrency: true },
  { code: "SGD", name: "Singapore Dollar", countryCode: "SG", countryName: "Singapore", symbol: "$", decimals: 2, region: "APAC", platformCurrency: false },
  { code: "AED", name: "UAE Dirham", countryCode: "AE", countryName: "United Arab Emirates", symbol: "د.إ", decimals: 2, region: "EMEA", platformCurrency: false },
  { code: "SAR", name: "Saudi Riyal", countryCode: "SA", countryName: "Saudi Arabia", symbol: "﷼", decimals: 2, region: "EMEA", platformCurrency: false },
  { code: "CAD", name: "Canadian Dollar", countryCode: "CA", countryName: "Canada", symbol: "$", decimals: 2, region: "AMER", platformCurrency: false },
  { code: "JPY", name: "Japanese Yen", countryCode: "JP", countryName: "Japan", symbol: "¥", decimals: 0, region: "APAC", platformCurrency: false },
  { code: "CNY", name: "Chinese Yuan", countryCode: "CN", countryName: "China", symbol: "¥", decimals: 2, region: "APAC", platformCurrency: false },
  { code: "HKD", name: "Hong Kong Dollar", countryCode: "HK", countryName: "Hong Kong", symbol: "$", decimals: 2, region: "APAC", platformCurrency: false },
  { code: "MYR", name: "Malaysian Ringgit", countryCode: "MY", countryName: "Malaysia", symbol: "RM", decimals: 2, region: "APAC", platformCurrency: false },
  { code: "PHP", name: "Philippine Peso", countryCode: "PH", countryName: "Philippines", symbol: "₱", decimals: 2, region: "APAC", platformCurrency: false },
  { code: "THB", name: "Thai Baht", countryCode: "TH", countryName: "Thailand", symbol: "฿", decimals: 2, region: "APAC", platformCurrency: false },
  { code: "KRW", name: "South Korean Won", countryCode: "KR", countryName: "South Korea", symbol: "₩", decimals: 0, region: "APAC", platformCurrency: false },
  { code: "CHF", name: "Swiss Franc", countryCode: "CH", countryName: "Switzerland", symbol: "CHF", decimals: 2, region: "EMEA", platformCurrency: false },
  { code: "SEK", name: "Swedish Krona", countryCode: "SE", countryName: "Sweden", symbol: "kr", decimals: 2, region: "EMEA", platformCurrency: false },
  { code: "ZAR", name: "South African Rand", countryCode: "ZA", countryName: "South Africa", symbol: "R", decimals: 2, region: "EMEA", platformCurrency: false },
];

/** Region each platform country sits in, for the default comparison set. */
const COUNTRY_REGIONS: Record<string, string> = {
  AU: "APAC",
  NZ: "APAC",
  IN: "APAC",
  GB: "EMEA",
  US: "AMER",
};

export function regionForCountry(countryCode: string | undefined): string {
  if (!countryCode) return "APAC";
  return COUNTRY_REGIONS[countryCode.toUpperCase()] || "APAC";
}

/**
 * The rows shown in the comparison table before the user expands it.
 *
 * Region-specific by design: an Australian customer cares about the
 * AUD→INR/SGD/NZD corridors, a UK one about EUR/AED. Every platform
 * currency is always included so the table always covers the currencies
 * their own cards can be denominated in.
 */
export function defaultComparisonCodes(
  currencies: Currency[],
  baseCountry: string | undefined,
  selectedCode: string,
): string[] {
  const region = regionForCountry(baseCountry);
  return currencies
    .filter(
      (c) => c.code !== selectedCode && (c.platformCurrency || c.region === region),
    )
    .map((c) => c.code);
}
